/**
 * Shared hook for audio pipeline stall detection and tiered recovery.
 *
 * Tiered recovery UX (designed to be non-alarming in medical settings):
 * - Tier 1 (immediate): Silent auto-recovery. Doctor never knows.
 * - Tier 2 (after 2+ failed attempts): Subtle toast. No action required.
 * - Tier 3 (after MAX_ATTEMPTS exhausted): Amber banner with manual recovery button.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { useToast } from '@/hooks';
import type { AudioPipelineHealth, PCMAudioManager } from '@/lib/pcmAudioManager';
import { Sentry } from '@/lib/sentry';
import type { StreamingClient } from '@/lib/streamingClient';

const MAX_RECOVERY_ATTEMPTS = 3;

interface UseAudioPipelineRecoveryOptions {
  pcmAudioManagerRef: React.RefObject<PCMAudioManager | null>;
  streamingClientRef: React.RefObject<StreamingClient | null>;
  /** Label for log messages, e.g. 'Conversation' or 'Dictation' */
  label: string;
  onMicrophoneError?: (error: unknown) => boolean;
  onMicrophoneReady?: () => void;
}

interface UseAudioPipelineRecoveryReturn {
  /** True when all auto-recovery attempts exhausted — show banner UI */
  pipelineStalled: boolean;
  /** Handler to pass to pcmAudioManager.onStall() */
  handlePipelineStall: (health: AudioPipelineHealth) => void;
  /** Handler for manual recovery button click */
  handleManualRecovery: () => Promise<void>;
  /** Call when starting a new recording to reset recovery state */
  resetRecoveryState: () => void;
}

export const useAudioPipelineRecovery = ({
  pcmAudioManagerRef,
  streamingClientRef,
  label,
  onMicrophoneError,
  onMicrophoneReady,
}: UseAudioPipelineRecoveryOptions): UseAudioPipelineRecoveryReturn => {
  const { toast } = useToast();
  const [pipelineStalled, setPipelineStalled] = useState(false);
  const recoveryAttemptsRef = useRef(0);
  const recoveryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRecoveringRef = useRef(false);

  // Clear any pending recovery timer
  const clearRecoveryTimer = useCallback(() => {
    if (recoveryTimerRef.current) {
      clearTimeout(recoveryTimerRef.current);
      recoveryTimerRef.current = null;
    }
  }, []);

  // Reset all recovery state (call when starting a new recording)
  const resetRecoveryState = useCallback(() => {
    clearRecoveryTimer();
    recoveryAttemptsRef.current = 0;
    isRecoveringRef.current = false;
    setPipelineStalled(false);
  }, [clearRecoveryTimer]);

  const attemptAutoRecovery = useCallback(async () => {
    // Prevent overlapping recovery attempts
    if (isRecoveringRef.current) return;
    if (!pcmAudioManagerRef.current || !streamingClientRef.current) return;

    isRecoveringRef.current = true;
    const attempt = ++recoveryAttemptsRef.current;

    if (attempt > MAX_RECOVERY_ATTEMPTS) {
      // Tier 3: All retries exhausted — show persistent banner
      isRecoveringRef.current = false;
      setPipelineStalled(true);
      Sentry.captureMessage('audio_stall_recovery_exhausted', {
        level: 'error',
        extra: pcmAudioManagerRef.current.getHealth() as unknown as Record<string, unknown>,
      });
      toast({
        title: 'Hljóðupptaka stöðvaðist',
        description: 'Endurræsing tókst ekki. Vinsamlegast endurnýjaðu síðuna.',
        variant: 'destructive',
        duration: 0, // Don't auto-dismiss
      });
      return;
    }

    try {
      // Flush server buffer before recovery (process pending audio cleanly)
      try {
        await streamingClientRef.current.flushBuffer({ detectTasks: false });
      } catch {
        // Continue even if flush fails
      }

      const recovery = await pcmAudioManagerRef.current.recover();
      if (recovery !== 'recovered') {
        isRecoveringRef.current = false;
        setPipelineStalled(true);
        toast({
          title: recovery === 'in-progress' ? 'Endurræsing er í gangi' : 'Hljóðupptaka er stöðvuð',
          description:
            recovery === 'in-progress'
              ? 'Bíddu eftir að endurræsingu lýkur.'
              : 'Hefðu upptöku aftur til að halda áfram.',
          duration: 5000,
        });
        return;
      }
      onMicrophoneReady?.();

      // Tier 1 success — silent recovery, doctor never knows
      isRecoveringRef.current = false;
      setPipelineStalled(false);
      console.info(`[${label}] Pipeline auto-recovered (attempt ${attempt})`);
      Sentry.captureMessage('audio_stall_auto_recovered', {
        level: 'warning',
        extra: {
          ...(pcmAudioManagerRef.current.getHealth() as unknown as Record<string, unknown>),
          recoveryAttempt: attempt,
        },
      });
    } catch (err) {
      isRecoveringRef.current = false;
      if (onMicrophoneError?.(err)) {
        clearRecoveryTimer();
        setPipelineStalled(true);
        return;
      }
      console.error(`[${label}] Auto-recovery attempt ${attempt} failed:`, err);

      if (attempt >= 2) {
        // Tier 2: Show non-alarming toast, retry
        toast({
          title: 'Hljóðgreining endurhleðst...',
          description: 'Reyni aftur...',
          duration: 3000,
        });
      }

      // Schedule next retry (clear any existing timer first)
      clearRecoveryTimer();
      recoveryTimerRef.current = setTimeout(() => attemptAutoRecovery(), 3000);
    }
  }, [pcmAudioManagerRef, streamingClientRef, label, toast, clearRecoveryTimer, onMicrophoneError, onMicrophoneReady]);

  const handlePipelineStall = useCallback(
    (_health: AudioPipelineHealth) => {
      console.error(`[${label}] Pipeline stall detected`);
      attemptAutoRecovery();
    },
    [label, attemptAutoRecovery]
  );

  const handleManualRecovery = useCallback(async () => {
    if (!pcmAudioManagerRef.current) return;

    // Clear any pending auto-recovery timer
    clearRecoveryTimer();
    isRecoveringRef.current = false;
    recoveryAttemptsRef.current = 0;
    setPipelineStalled(false);

    try {
      const recovery = await pcmAudioManagerRef.current.recover();
      if (recovery !== 'recovered') {
        setPipelineStalled(true);
        toast({
          title: recovery === 'in-progress' ? 'Endurræsing er í gangi' : 'Hljóðupptaka er stöðvuð',
          description:
            recovery === 'in-progress'
              ? 'Bíddu eftir að endurræsingu lýkur.'
              : 'Hefðu upptöku aftur til að halda áfram.',
          duration: 5000,
        });
        return;
      }
      onMicrophoneReady?.();
      toast({
        title: 'Hljóðnemi endurræstur',
        description: 'Upptaka heldur áfram.',
        duration: 3000,
      });
    } catch (err) {
      if (onMicrophoneError?.(err)) {
        setPipelineStalled(true);
        return;
      }
      console.error(`[${label}] Manual recovery failed:`, err);
      toast({
        title: 'Endurræsing tókst ekki',
        description: 'Vinsamlegast endurnýjaðu síðuna.',
        variant: 'destructive',
        duration: 0,
      });
    }
  }, [pcmAudioManagerRef, label, toast, clearRecoveryTimer, onMicrophoneError, onMicrophoneReady]);

  // Clean up recovery timer on unmount
  useEffect(() => {
    return () => {
      clearRecoveryTimer();
    };
  }, [clearRecoveryTimer]);

  return {
    pipelineStalled,
    handlePipelineStall,
    handleManualRecovery,
    resetRecoveryState,
  };
};
